import type {
  AddItemMessage,
  CartFormInput,
  CartItem,
  PantryActionBy,
  RemoveItemsMessage,
  ServerMessage,
} from 'types';

import { useStorage } from '@vueuse/core';
import isEqual from 'lodash/isEqual';
import { v7 as uuid } from 'uuid';

import ingredients from './ingredients.json';
import { usePartySocket } from './usePartySocket';

export interface CartSuggestion {
  name: string;
  cartId?: string;
}

const INPUT_COLLAPSED_PLACEHOLDER = ' +   ADD';

export function useCart() {
  const config = useRuntimeConfig();

  const cart = ref<CartItem[]>([]);
  const lastSynced = ref<number>(0);
  const selectedIngredients = useStorage<Record<string, number>>(
    'juss-selectedIngredients',
    {},
  );
  const loading = ref(true);
  /** True once the pantry has sent the cart, so a check against it is real. */
  const synced = ref(false);
  /** The cart as the pantry last sent it. Echoing that back as `set-cart`
   * would overwrite writes other devices made in the meantime, such as a burst
   * of `add-item` from the menu's add to cart button. */
  let serverCart = '';

  const clientId = uuid();
  const { deviceId, persona, connectionQuery } = usePantryAuth();
  const party = usePartySocket<string>({
    host: config.public.pantryHost || 'pantry.juss.recipes',
    party: 'cart',
    room: 'juss-recipes-cart',
    immediate: false,
    autoReconnect: true,
    // Sign the connection with the device token on every (re)connect; keep the
    // client id so the server retains its per-connection identity.
    query: async () => {
      // The token's own id: the server issued it, so it is the one signed.
      const token = await connectionQuery();
      return {
        id: clientId,
        deviceId: token.deviceId,
        timestamp: token.timestamp,
        signature: token.signature,
      };
    },
    id: clientId,
  });

  const cartInput = ref<CartFormInput>({ name: '' });
  const cartInputRef = ref<HTMLInputElement>();
  const cartInputFocussed = ref(false);
  const cartInputExpanded = ref(false);
  const cartInputPlaceholder = ref(INPUT_COLLAPSED_PLACEHOLDER);

  const completeExpanded = ref(false);

  const suggestions = reactive<
    Record<'From Cart' | 'Suggestions', CartSuggestion[]>
  >({
    'From Cart': [],
    Suggestions: [],
  });

  const sortedCart = computed({
    get: () =>
      cart.value.sort((a, b) => Number(a.complete) - Number(b.complete)),
    set: (newCart) => (cart.value = newCart),
  });

  const incompleteCartRef = computed(() =>
    sortedCart.value.filter(({ complete }) => !complete),
  );
  const completeCartRef = computed(() =>
    sortedCart.value.filter(({ complete }) => complete),
  );

  // writable computed refs
  const incompleteCart = computed({
    get: () => incompleteCartRef.value,
    set: (newIncomplete) =>
      (cart.value = [...newIncomplete, ...completeCartRef.value]),
  });
  const completeCart = computed({
    get: () => completeCartRef.value,
    set: (newComplete) =>
      (cart.value = [...incompleteCartRef.value, ...newComplete]),
  });

  const sortedSelectedIngredients = computed(() => {
    return Object.entries(selectedIngredients.value)
      .toSorted((a, b) => a[1] - b[1])
      .map((x) => x[0]);
  });

  // init client
  onMounted(() => {
    // backwards compatibility between old storage key
    // TODO: remove after some time
    const oldCartStorage = window.localStorage.getItem('cart');
    const oldCart = (
      oldCartStorage ? JSON.parse(oldCartStorage) : []
    ) as CartItem[];
    if (cart.value.length === 0 && oldCart.length > 0) cart.value = oldCart;

    loading.value = false;
    party
      .open()
      .catch((e) => console.error('Could not connect to the cart', e));
  });

  // watch for party messages
  watch(
    party.data,
    (newData) => {
      try {
        if (!newData) return;
        const message = JSON.parse(newData) as ServerMessage;
        handlePartyMessage(message);
      } catch (e) {
        console.error('Error parsing message', { newData, error: e });
        return;
      }
    },
    { immediate: true },
  );

  // flush cart updates to server, at most every 500ms. A change inside that
  // window is sent when it ends rather than dropped, so two quick menu add to
  // cart presses both reach the pantry.
  let flushTimer: ReturnType<typeof setTimeout> | undefined;
  function flush() {
    flushTimer = undefined;
    if (JSON.stringify(cart.value) === serverCart) return;
    lastSynced.value = Date.now();
    party.send(JSON.stringify({ action: 'set-cart', cart: cart.value }));
  }
  watch(
    cart,
    () => {
      if (flushTimer) return;
      const wait = lastSynced.value + 500 - Date.now();
      if (wait > 0) flushTimer = setTimeout(flush, wait);
      else flush();
    },
    { deep: true },
  );
  onBeforeUnmount(() => {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flush();
    }
  });

  watch(
    [cartInput, sortedCart],
    () => {
      const search = cartInput.value.name;
      suggestions['From Cart'] = [];
      suggestions.Suggestions = [];
      if (!search && !cartInputFocussed.value) cartInputExpanded.value = false;
      if (search.length >= 2) {
        suggestions['From Cart'] = sortedCart.value
          .filter(
            ({ name, complete }) =>
              complete && name.toLowerCase().includes(search.toLowerCase()),
          )
          .map(({ id, name }) => ({ cartId: id, name }));

        const cartItems = sortedCart.value.map(({ name }) =>
          name.toLowerCase(),
        );
        suggestions.Suggestions = filterIngredients(search)
          .filter((name) => !cartItems.includes(name.toLowerCase()))
          .map((name) => ({ name }));
      }
    },
    { deep: true },
  );

  function getActionBy(): PantryActionBy {
    if (!deviceId.value || !persona.value)
      throw new Error('Missing deviceId or persona');
    return {
      deviceId: deviceId.value,
      persona: persona.value,
    };
  }

  function handlePartyMessage(message: ServerMessage | null) {
    if (!message) return;
    switch (message.action) {
      case 'init':
      case 'cart-updated':
        synced.value = true;
        serverCart = JSON.stringify(message.cart);
        // A local change is waiting to be sent; it replaces this cart then.
        if (flushTimer) break;
        // check to see if the cart has changed before updating
        if (isEqual(cart.value, message.cart)) break;
        cart.value = message.cart;
        break;
    }
  }

  function toggleComplete(item: CartItem) {
    const index = cart.value.findIndex((i) => i.id === item.id);
    if (index === -1) return;
    cart.value[index] = {
      ...item,
      complete: !item.complete,
      updated: new Date().toISOString(),
      updatedBy: getActionBy(),
    };
    return cart.value[index];
  }

  function addItem(item: CartFormInput) {
    // add ingredients to selected ingredients list for better sorting
    if (ingredients.includes(item.name))
      selectedIngredients.value[item.name] = Date.now();

    // A set-cart still waiting would overwrite this add, so it goes first.
    if (flushTimer) {
      clearTimeout(flushTimer);
      flush();
    }
    // Pantry dedupes the line, splits "2 lbs green beans" into a name and an
    // amount, and echoes the cart.
    return party.send(
      JSON.stringify({ action: 'add-item', item } satisfies AddItemMessage),
    );
  }

  function removeItem(item: CartItem) {
    const index = cart.value.findIndex((i) => i.id === item.id);
    if (index === -1) return;
    cart.value.splice(index, 1);
  }

  /** Drops the items here and sends `remove-items`. Pantry skips ids
   * another device already removed. */
  function removeItems(ids: CartItem['id'][]) {
    const removed = new Set(ids);
    cart.value = cart.value.filter(({ id }) => !removed.has(id));
    party.send(
      JSON.stringify({
        action: 'remove-items',
        ids,
      } satisfies RemoveItemsMessage),
    );
  }

  function updateItem(item: CartItem) {
    const index = cart.value.findIndex((i) => i.id === item.id);
    if (index === -1) return;
    cart.value[index] = {
      ...cart.value[index],
      ...item,
      updated: new Date().toISOString(),
      updatedBy: getActionBy(),
    };
  }

  function filterIngredients(search?: string) {
    const list = ingredients.filter((i) =>
      search ? i.toLowerCase().includes(search.toLowerCase()) : true,
    );
    const selected = list
      .filter((i) => sortedSelectedIngredients.value.includes(i))
      .toSorted((a, b) => {
        const aIndex = sortedSelectedIngredients.value.indexOf(a);
        const bIndex = sortedSelectedIngredients.value.indexOf(b);
        return bIndex > aIndex ? 1 : -1;
      });
    const rest = list
      .filter((i) => !sortedSelectedIngredients.value.includes(i))
      .toSorted((a, b) => {
        if (search) {
          const key = search.toLowerCase();
          const isGoodMatchA = a.toLowerCase().startsWith(key);
          const isGoodMatchB = b.toLowerCase().startsWith(key);
          if (isGoodMatchA !== isGoodMatchB) return isGoodMatchA ? -1 : 1; // XOR
        }
        return a.localeCompare(b);
      });
    return [...selected, ...rest];
  }

  function randomIngredient() {
    const list =
      sortedSelectedIngredients.value.length > 3
        ? sortedSelectedIngredients.value
        : filterIngredients();
    return list[Math.floor(Math.random() * list.length)];
  }

  function cartInputFocusIn() {
    cartInputPlaceholder.value = randomIngredient() + '?';
    cartInputFocussed.value = true;
    cartInputExpanded.value = true;
  }

  function cartInputFocusOut() {
    cartInputPlaceholder.value = INPUT_COLLAPSED_PLACEHOLDER;
    cartInputFocussed.value = false;
    if (!cartInput.value.name) cartInputExpanded.value = false;
  }

  return {
    cart,
    loading,
    synced,
    connected: party.connected,
    authError: party.error,
    deviceId,
    persona,
    sortedCart,
    incompleteCart,
    completeCart,
    cartInput,
    cartInputRef,
    cartInputExpanded,
    cartInputPlaceholder,
    completeExpanded,
    suggestions,
    toggleComplete,
    addItem,
    removeItem,
    removeItems,
    updateItem,
    filterIngredients,
    cartInputFocusIn,
    cartInputFocusOut,
  };
}
